import React from 'react';
import { Link } from "react-router-dom";
import { FaRocket } from 'react-icons/fa';
import BackgroundBeams_1 from '../../components/ui/BackgroundBeams_1.jsx';
import Language_i18 from '../../js/useNationalLanguage.jsx';

export default function HomeBirdHero({
  className = "",
  glassPanel,
  ctaLinkProps,
  ctaLabel,
  hideCtaIcon = false,
  docLinkProps,
}) {
  const { lang } = Language_i18();
  const safeLang = lang == 'vi' ? 'vi' : 'en';

  const text = {
    en: {
      title: "Free Bird",
      intro: "Bird is a media company focused on integrated campaigns, media operations, and performance growth across digital channels.",
      docs: "Documentation",
      hub: "Bird Advertising Hub",
      profileTag: "Advertising Services",
      profileTitle: "Service Overview Snapshot",
      legalTitle: "Service Positioning",
      linesTitle: "Core Advertising Services",
      overview: [
        { label: "Status", value: "Active" },
        { label: "Business Type", value: "Advertising Company" },
        { label: "Service Orientation", value: "Integrated execution model across strategy, media, and performance" },
      ],
      legal: [
        { label: "Company Name", value: "BIRD ADVERTISING COMPANY" },
        { label: "Main Focus", value: "Integrated advertising campaigns and media execution" },
        { label: "Delivery Model", value: "End-to-end planning, execution, and optimization" },
        { label: "Performance Goal", value: "Reach growth, lead quality, and conversion efficiency" },
        { label: "Client Segment", value: "Businesses and brands scaling digital promotion" },
      ],
      industries: [
        "Integrated advertising campaigns",
        "Brand strategy and creative production",
        "Media buying and channel optimization",
        "Performance marketing and reporting",
      ],
    },
    vi: {
      title: "Free Bird",
      intro: "Bird là công ty truyền thông, tập trung vào chiến dịch tích hợp, vận hành truyền thông và tăng trưởng hiệu quả trên các kênh số.",
      docs: "Tài liệu",
      hub: "Trung Tâm Truyền Thông",
      profileTag: "Dịch Vụ Quảng Cáo",
      profileTitle: "Tổng Quan Dịch Vụ",
      legalTitle: "Định Vị Dịch Vụ",
      linesTitle: "Nhóm Dịch Vụ Quảng Cáo Cốt Lõi",
      overview: [
        { label: "Trạng thái", value: "Đang hoạt động" },
        { label: "Loại hình", value: "Công ty quảng cáo" },
        { label: "Định hướng dịch vụ", value: "Mô hình triển khai tích hợp từ chiến lược, media đến hiệu quả" },
      ],
      legal: [
        { label: "Tên công ty", value: "BIRD ADVERTISING COMPANY" },
        { label: "Trọng tâm chính", value: "Triển khai chiến dịch quảng cáo tích hợp và vận hành media" },
        { label: "Mô hình triển khai", value: "Lên kế hoạch, triển khai và tối ưu end-to-end" },
        { label: "Mục tiêu hiệu quả", value: "Tăng độ phủ, chất lượng khách hàng và hiệu suất chuyển đổi" },
        { label: "Nhóm khách hàng", value: "Doanh nghiệp và thương hiệu cần mở rộng quảng bá số" },
      ],
      industries: [
        "Triển khai chiến dịch quảng cáo tích hợp",
        "Chiến lược thương hiệu và sản xuất sáng tạo",
        "Mua media và tối ưu kênh truyền thông",
        "Performance marketing và báo cáo hiệu quả",
      ],
    },
  };
  const t = text[safeLang];

  return (
    <div className={`space-y-4 sm:space-y-5 ${className}`.trim()}>
      <div className={`${glassPanel} enter-hero`}>
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-sky-500/15 to-transparent rounded-full blur-3xl -z-10"></div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 sm:gap-8 items-center">
          <div className="md:col-span-3 space-y-4 sm:space-y-6">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight">
              <span className="bg-gradient-to-r from-white via-sky-200 to-cyan-300 bg-clip-text text-transparent filter drop-shadow-lg">
                {t.title}
              </span>
            </h1>

            <p className="text-[16px] sm:text-lg text-slate-200 leading-relaxed font-light border-l-4 border-sky-400 pl-4">
              {t.intro}
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 pt-1 sm:pt-2">
              <Link
                {...ctaLinkProps}
                className="relative inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-gradient-to-r from-sky-600 to-cyan-600 text-white font-semibold text-sm sm:text-base py-2.5 px-6 sm:px-7 rounded-full no-underline shadow-[0_0_20px_rgba(14,165,233,0.38)] animate-pulse motion-reduce:animate-none hover:shadow-[0_0_36px_rgba(14,165,233,0.55)] hover:scale-105 transition-all duration-300"
              >
                {!hideCtaIcon ? <FaRocket /> : null} {ctaLabel}
              </Link>
              <Link
                {...docLinkProps}
                className="w-full sm:w-auto px-6 sm:px-7 py-2.5 rounded-full border border-white/20 text-white text-sm sm:text-base no-underline shadow-[0_0_10px_rgba(14,165,233,0.2)] hover:bg-white/10 hover:shadow-[0_0_18px_rgba(14,165,233,0.32)] transition-all font-semibold"
              >
                {t.docs}
              </Link>
            </div>
          </div>

          <div className="md:col-span-2 relative">
            <div className="block h-[200px] sm:h-[290px] w-full sm:max-w-[390px] ml-auto rounded-xl border border-white/15 bg-slate-900/35 p-3 sm:p-3.5 relative flex flex-col items-center justify-center antialiased overflow-hidden transition hover:border-sky-400/60 hover:bg-slate-900/45">
              <div className="z-10 w-full px-3 text-center text-lg sm:px-5 sm:text-2xl font-bold text-slate-100">
                {t.hub}
              </div>
              <BackgroundBeams_1 />
            </div>
          </div>
        </div>
      </div>

      <section className={`${glassPanel} enter-stack`}>
        <div className="mb-1 text-xs uppercase tracking-[0.22em] text-slate-300/80">
          {t.profileTag}
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-white">
          {t.profileTitle}
        </h2>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {t.overview.map((item) => (
            <div
              key={item.label}
              className="rounded-xl border border-white/15 bg-slate-900/35 p-3"
            >
              <div className="text-xs uppercase tracking-[0.12em] text-slate-300">{item.label}</div>
              <div className="mt-1 text-sm font-semibold text-white">{item.value}</div>
            </div>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-5">
          <div className="lg:col-span-3 rounded-xl border border-white/15 bg-slate-900/35 p-4">
            <div className="text-sm font-semibold text-sky-200">{t.legalTitle}</div>
            <div className="mt-3 space-y-2">
              {t.legal.map((item) => (
                <div key={item.label} className="flex flex-col gap-1 border-b border-white/10 pb-2 last:border-b-0">
                  <div className="text-xs uppercase tracking-[0.1em] text-slate-300">{item.label}</div>
                  <div className="text-sm text-white">{item.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 rounded-xl border border-white/15 bg-slate-900/35 p-4">
            <div className="text-sm font-semibold text-sky-200">{t.linesTitle}</div>
            <div className="mt-3 space-y-2">
              {t.industries.map((line) => (
                <div key={line} className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-100">
                  {line}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
